import * as Enumerable from 'linq';

import { Source } from '../../enums/enum.source';
import { Entity, ShareableEntity } from '../entity';
import { ActivityEntityName } from './activity.entity.name';
import { ActivityType } from './activity.type';

export enum LabelType {
    Job = 'Job',
    Quote = 'Quote',
    Customer = 'Customer',
    User = 'User',
    Overheads = 'Overheads',
    Form = 'Form',
    Finance = 'Finance',
    System = 'System',
    WorkOrderSystem = 'WorkOrderSystem',
    Account = 'Account',
    Contact = 'Contact',
    Zone = 'Zone',
    WorkCategory = 'Work Categories',
    LeadSource = 'Lead Source',
    Lead = 'Lead',
    Opportunity = 'Opportunity',
    OpportunityLost = 'Opportunity Lost',
    Project = 'Project',
    ProjectType = 'Project Type',
    PaymentType = 'Payment Type',
    File = 'File',
    Asset = 'Asset',
    ItemLocation = 'Item Location',
    Task = 'Task',
    Phase = 'Phase',
    Site = 'Site',
    Scheduling = 'Scheduling',
    ServiceType = 'Service Type',
    ServiceScope = 'Service Scope',
    Subscriptions = 'Subscriptions',
    Agreement = 'Agreement',
    Work = 'Work',
    IntegrationTypes = 'Integration Types',
    ThreadTypes = 'Thread Types',
    FlagCategory = 'Flag Category',
    ContactLinkRelationship = 'Contact Link Relationship',
    EvidenceException = 'Evidence Exception'
}

export enum AlertPriority {
    Critical = 'Critical',
    High = 'High',
    Medium = 'Medium',
    Low = 'Low'
}

export interface AlertOptions {
    enable: boolean;
    icon?: string;
    priority?: AlertPriority;
    message?: string;
    allowCustomNote?: boolean;
    /** Field App must capture an acknowledgment for this alert; only meaningful while alertDisplay.showInFieldApp is on. */
    requireAcknowledgement?: boolean;
    alertDisplay?: DisplayOptions;
    entitiesDisplay?: ActivityEntityName[];
}

export interface DisplayOptions {
    showInFieldApp: boolean;
    showInClientPortal: boolean;
    showInContractorPortal: boolean;
}

export interface Label extends LabelEntity {
    alert?: AlertOptions;
    display?: DisplayOptions;
}

export interface ServiceTypeTag extends Label {
    data?: {
        /** Only allow single service - can only add this service to a site or asset once. */
        multipleInstances?: boolean
    }
}


export interface LabelEntity extends ShareableEntity {
    icon?: string;
    type: LabelType;
}

/**
 * Normalizes Tag field `typeData` (single LabelType or LabelType[]) for pickers.
 * Empty / unknown shapes → [].
 */
export function resolveLabelTypes(typeData: unknown): LabelType[] {

    if (typeData == null || typeData === '') return [];
    if (Array.isArray(typeData)) {
        return typeData.filter((t): t is LabelType => typeof t === 'string' && !!t);
    }
    if (typeof typeData === 'string') return [typeData as LabelType];
    return [];
}

/** Primary label type for assignment editors (first of multi-type typeData). */
export function resolvePrimaryLabelType(typeData: unknown): LabelType | undefined {

    return resolveLabelTypes(typeData)[0];
}

/** Shape shared with PickerTagRequest without importing the pickers module (avoids cycles). */
export type LabelPickerTypeRequest = {
    type?: LabelType;
    types?: LabelType[];
    includeSystemTags?: boolean;
    search?: string;
    [key: string]: unknown;
};

/**
 * Builds a tag picker request from metadata typeData.
 * Multi-type fields set both `types` and `type` (first entry) for backward compatibility.
 */
export function toPickerTagRequest(typeData: unknown, extras?: Omit<LabelPickerTypeRequest, 'type' | 'types'>): LabelPickerTypeRequest {

    const types = resolveLabelTypes(typeData);
    const request: LabelPickerTypeRequest = {
        ...(extras ?? {}),
        type: types[0],
    };
    if (types.length > 1) request.types = types;
    return request;
}

/** Resolves effective types from a picker request (`types` wins over scalar `type`). */
export function resolvePickerTagTypes(request?: { type?: LabelType; types?: LabelType[] } | null): LabelType[] {

    if (request?.types?.length) return request.types.filter(Boolean);
    if (request?.type) return [request.type];
    return [];
}


export class SystemLabels {

    /** This appears for Jobs and WorkOrders that are participating in claims. In upstream tenancy (eg. Asset) this will be on the WorkOrder and for downstream (Contractor i.e Alectrix) this will be on the job. All claim tags are mutually exclusive i.e none or only 1 */
    public static claimPending: Label = { _id: '00000000-0000-0000-0000-00000000003', name: 'Claim Pending', type: LabelType.WorkOrderSystem };

    /** This label will be set on the Job when a downstream contractor (Alextrix) submits a claim upstream (Assett). All claim tags are mutually exclusive i.e none or only 1*/
    public static claimSubmitted: Label = { _id: '00000000-0000-0000-0000-00000000004', name: 'Claim Submitted', type: LabelType.WorkOrderSystem };

    /** This label will be on the WorkOrder for upstream tenancy ie. This occurs when a downstream tenancy (Alextrix) or a contractor using the portal submits a claim. All claim tags are mutually exclusive i.e none or only 1  */
    public static claimReceived: Label = { _id: '00000000-0000-0000-0000-00000000007', name: 'Claim Received', type: LabelType.WorkOrderSystem };

    /** Upstream-stage only (SDD 1.6 §5.1.4): applied instead of claimReceived when a claim is submitted after having been returned (claimReturnCount > 0). Indicates a second-chance submission; never blocks further returns. The downstream job keeps claimSubmitted. All claim tags are mutually exclusive i.e none or only 1 */
    public static claimResubmitted: Label = { _id: '00000000-0000-0000-0000-00000000012', name: 'Claim Resubmitted', type: LabelType.WorkOrderSystem };

    /** This label will be on the WorkOrder when an upstream contractor (Assett) approves a claim. It will also appear on the Job for a downstream tenancy (Alectrix)  All claim tags are mutually exclusive i.e none or only 1*/
    public static claimApproved: Label = { _id: '00000000-0000-0000-0000-00000000005', name: 'Claim Approved', type: LabelType.WorkOrderSystem };

    /** This label will be on the WorkOrder when an upstream contractor (Assett) rejects a claim. It will also appear on the Job for a downstream tenancy (Alectrix)  All claim tags are mutually exclusive i.e none or only 1*/
    public static claimReturned: Label = { _id: '00000000-0000-0000-0000-00000000006', name: 'Claim Returned', type: LabelType.WorkOrderSystem };

    /** This label will be on the WorkOrder when an upstream tenant rejects a claim outright. It will also appear on the Job for a downstream tenancy. All claim tags are mutually exclusive i.e none or only 1*/
    public static claimRejected: Label = { _id: '00000000-0000-0000-0000-00000000011', name: 'Claim Rejected', type: LabelType.WorkOrderSystem };

    /** Set on the upstream WorkOrder when a Connect downstream tenant fails to import the job.
     *  Replaces claimPending. Downstream admin must fix their config and retry. Mutually exclusive with claim labels. */
    public static connectImportFailed: Label = { _id: '00000000-0000-0000-0000-00000000009', name: 'Connect Import Failed', type: LabelType.WorkOrderSystem };

    // Previously used _id ...00000000007 which collided with `claimReceived` above.
    // The label is short-lived (added at bulk invoice start, removed at end), so a
    // simple constant renumber is safe — no persisted-data migration needed in steady
    // state; any in-flight bulk run during deploy reconciles on the next unlock.
    public static jobBulkInvoiceLocked: Label = { _id: '00000000-0000-0000-0000-00000000010', name: 'Bulk Invoice Processing', type: LabelType.Job };

    /** This label is automatically added to bills that are generated by AI from PDF documents */
    public static aiGeneratedBill: Label = { _id: '00000000-0000-0000-0000-00000000008', name: 'AI Generated', type: LabelType.Finance, icon: 'pi pi-sparkles', color: '#8B5CF6' };

    public static tenant = {
        //This unassigned is different to the contractor unassigned (i.e. not even assigned to a contractor)
        unassignedWorkOrder: { _id: '00000000-0000-0000-0000-00000000001', name: 'Unassigned', type: LabelType.WorkOrderSystem } as Label,
        claimPending: this.claimPending,
        claimSubmitted: this.claimSubmitted,
        claimApproved: this.claimApproved,
        claimReturned: this.claimReturned,
        claimRejected: this.claimRejected,
        claimReceived: this.claimReceived,
        claimResubmitted: this.claimResubmitted,
        connectImportFailed: this.connectImportFailed,
    };

    public static contractor = {
        //This unassigned is different to the tenant unassigned (i.e. its been assigned to a contractor but we dont have a login contact on it yet, only relevant in portal)
        unassignedWorkOrder: { _id: '00000000-0000-0000-0000-00000000002', name: 'Unassigned', type: LabelType.WorkOrderSystem, showOn: [Source.ContractorPortal] } as Label,
        claimPending: this.claimPending,
        claimSubmitted: this.claimSubmitted,
        claimReceived: this.claimReceived, //Don't remove this, this is used in the contractor portal
        claimResubmitted: this.claimResubmitted,
        claimApproved: this.claimApproved,
        claimReturned: this.claimReturned,
        claimRejected: this.claimRejected
    }

    public static claimLabels = [
        this.claimPending,
        this.claimSubmitted,
        this.claimReceived,
        this.claimResubmitted,
        this.claimApproved,
        this.claimReturned,
        this.claimRejected
    ]

    public static claimLabelMap = new Map<string, Label>(this.claimLabels.map(x => [x._id, x]));

    /**
     * All system-defined label ids. Used by UIs (e.g. bulk-action Tag pickers)
     * that need to suppress system labels — users cannot add or remove them.
     */
    public static getAllSystemIds(): Set<string> {

        return new Set<string>([
            SystemLabels.claimPending._id,
            SystemLabels.claimSubmitted._id,
            SystemLabels.claimReceived._id,
            SystemLabels.claimResubmitted._id,
            SystemLabels.claimApproved._id,
            SystemLabels.claimReturned._id,
            SystemLabels.claimRejected._id,
            SystemLabels.connectImportFailed._id,
            SystemLabels.jobBulkInvoiceLocked._id,
            SystemLabels.aiGeneratedBill._id,
            SystemLabels.tenant.unassignedWorkOrder._id,
            SystemLabels.contractor.unassignedWorkOrder._id,
        ]);
    }

    /**
     * True when the given label is a built-in system label (by id or by type).
     * Type covers user-created labels of System/WorkOrderSystem types.
     */
    public static isSystem(label: { _id?: string; type?: LabelType } | null | undefined): boolean {

        if (!label) return false;
        if (label.type === LabelType.System || label.type === LabelType.WorkOrderSystem) return true;
        return label._id ? SystemLabels.getAllSystemIds().has(label._id) : false;
    }

    public static client = {};

    public static contacts = {
        owner: { _id: '00000000-0000-0000-0001-00000000001', name: 'Owner', type: LabelType.Contact },
        scim: { _id: '00000000-0000-0000-0001-00000000002', name: 'SCIM', type: LabelType.Contact }
    }

    public static isVisible(entity: Entity, destination: Source): boolean {

        if (!entity || !destination) return false;

        switch (destination) {
            case Source.Web: {
                return Enumerable
                    .from(SystemLabels.tenant)
                    .any(x => (x.value as Label)?._id === entity._id);
            }

            case Source.ContractorPortal: {
                return Enumerable
                    .from(SystemLabels.contractor)
                    .any(x => (x.value as Label)?._id === entity._id);
            }

            default:
                return false;
        }
    }

    public static forDestination(labels: Entity[], destination: Source) {
        return Enumerable
            .from(labels)
            .where(l => SystemLabels.isVisible(l, destination))
            .toArray();
    }

}

export const getClaimActivityName = (label: Entity, upstream: boolean) => {

    let labels: Record<string, string> = null;

    if (upstream) {

        labels = {
            [SystemLabels.claimReceived._id]: `Claim was received from contractor`,
            [SystemLabels.claimResubmitted._id]: `Claim was resubmitted by contractor`,
            [SystemLabels.claimApproved._id]: `Claim was approved`,
            [SystemLabels.claimReturned._id]: `Claim was returned to contractor`,
            [SystemLabels.claimRejected._id]: `Claim was rejected`,
        };

    } else {

        labels = {
            [SystemLabels.claimSubmitted._id]: `Claim was submitted`,
            [SystemLabels.claimApproved._id]: `Claim was approved`,
            [SystemLabels.claimReturned._id]: `Claim was returned for rework`,
            [SystemLabels.claimRejected._id]: `Claim was rejected`,
        }

    }

    return labels[label._id];
}

/** Maps a claim system label to the typed ActivityType stamped on its claim activity. The upstream (claimReceived) and downstream (claimSubmitted) submission labels share ClaimSubmitted; a return is ClaimRework on both sides. */
export const getClaimActivityType = (label: Entity): ActivityType => {

    const types: Record<string, ActivityType> = {
        [SystemLabels.claimReceived._id]: ActivityType.ClaimSubmitted,
        [SystemLabels.claimSubmitted._id]: ActivityType.ClaimSubmitted,
        [SystemLabels.claimResubmitted._id]: ActivityType.ClaimResubmitted,
        [SystemLabels.claimApproved._id]: ActivityType.ClaimApproved,
        [SystemLabels.claimReturned._id]: ActivityType.ClaimRework,
        [SystemLabels.claimRejected._id]: ActivityType.ClaimRejected,
    };

    return types[label._id] ?? null;
}
